/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// NewProduct page components
import FormField from "layouts/ecommerce/products/new-product/components/FormField";

// Import variabel API
import { API_HOHOLOK } from "utils";

// Fungsi format rupiah
function formatRupiah(angka) {
  if (!angka) return "";
  const numberString = angka.replace(/[^\d]/g, "");
  const sisa = numberString.length % 3;
  let rupiah = numberString.substr(0, sisa);
  const ribuan = numberString.substr(sisa).match(/\d{3}/g);
  if (ribuan) {
    rupiah += (sisa ? "." : "") + ribuan.join(".");
  }
  return rupiah ? "Rp " + rupiah : "";
}

function ProyekDetail({ values = {}, onChange, errors = {} }) {
  // State untuk data desa dan kecamatan
  const [desaList, setDesaList] = useState([]);
  const [kecamatanOptions, setKecamatanOptions] = useState([]);
  const [desaOptions, setDesaOptions] = useState([]);
  const [loadingDesa, setLoadingDesa] = useState(true);

  // Fetch data desa dari endpoint
  useEffect(() => {
    setLoadingDesa(true);
    axios
      .get(`${API_HOHOLOK}desa/`)
      .then((res) => {
        setDesaList(res.data);
        const kecamatanSet = Array.from(
          new Set(res.data.map((item) => item.kecamatan))
        );
        setKecamatanOptions(kecamatanSet);
        setLoadingDesa(false);
      })
      .catch((err) => {
        setDesaList([]);
        setKecamatanOptions([]);
        setLoadingDesa(false);
      });
  }, []);

  // Update desaOptions jika kecamatan berubah
  useEffect(() => {
    if (values.kecamatan) {
      const filtered = desaList
        .filter((item) => item.kecamatan === values.kecamatan)
        .map((item) => item.desa);
      setDesaOptions(filtered);
      // Debug log
      console.log("Desa options untuk", values.kecamatan, ":", filtered);
    } else {
      setDesaOptions([]);
    }
  }, [values.kecamatan, desaList]);

  // Handler untuk input biasa
  const handleInputChange = (field) => (event) => {
    onChange({ [field]: event.target.value });
  };

  // Handler khusus untuk input koordinat (longitude/latitude)
  const handleCoordinateChange = (field) => (event) => {
    let value = event.target.value;
    // Hanya izinkan angka, titik, dan minus
    value = value.replace(/[^0-9.-]/g, "");
    onChange({ [field]: value });
  };

  return (
    <MDBox>
      <MDTypography variant="h5">Proyek Detail</MDTypography>
      <MDBox mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type="text"
              label="Nama Proyek"
              value={values.nama_proyek}
              onChange={handleInputChange("nama_proyek")}
              required
              error={Boolean(errors && errors.nama_proyek)}
            />
            {errors && errors.nama_proyek && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.nama_proyek}
              </span>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormField
              type="datetime-local"
              label="Start"
              value={values.tanggal_mulai}
              onChange={handleInputChange("tanggal_mulai")}
              required
              error={Boolean(errors && errors.tanggal_mulai)}
            />
            {errors && errors.tanggal_mulai && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.tanggal_mulai}
              </span>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormField
              type="datetime-local"
              label="Finish"
              value={values.tanggal_selesai}
              onChange={handleInputChange("tanggal_selesai")}
              required
              error={Boolean(errors && errors.tanggal_selesai)}
            />
            {errors && errors.tanggal_selesai && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.tanggal_selesai}
              </span>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <MDBox mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={kecamatanOptions}
              value={values.kecamatan || ""}
              onChange={(event, newValue) => {
                onChange({ kecamatan: newValue || "", desa: "" });
              }}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  label="Kecamatan"
                  variant="standard"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(errors && errors.kecamatan)}
                />
              )}
              disabled={loadingDesa}
              required
            />
            {errors && errors.kecamatan && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.kecamatan}
              </span>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={desaOptions}
              value={values.desa || ""}
              onChange={(event, newValue) => {
                onChange({ desa: newValue || "" });
              }}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  label="Desa/Kelurahan"
                  variant="standard"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(errors && errors.desa)}
                />
              )}
              disabled={!values.kecamatan}
              required
            />
            {errors && errors.desa && (
              <span style={{ color: "red", fontSize: 12 }}>{errors.desa}</span>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <MDBox mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField
              type="number"
              label="Total Anggaran"
              value={values.total_anggaran || ""}
              onChange={(e) => {
                // Simpan nilai angka (bisa decimal)
                const value = e.target.value;
                onChange({ total_anggaran: value });
              }}
              step="0.01"
              min="0"
              required
              error={Boolean(errors && errors.total_anggaran)}
            />
            {errors && errors.total_anggaran && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.total_anggaran}
              </span>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <MDBox mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type="text"
              label="Longitude"
              value={values.longitude}
              onChange={handleCoordinateChange("longitude")}
              placeholder="Contoh: 123.456789"
              required
              error={Boolean(errors && errors.longitude)}
            />
            {errors && errors.longitude && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.longitude}
              </span>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              type="text"
              label="Latitude"
              value={values.latitude}
              onChange={handleCoordinateChange("latitude")}
              placeholder="Contoh: -10.123456"
              required
              error={Boolean(errors && errors.latitude)}
            />
            {errors && errors.latitude && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.latitude}
              </span>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <MDBox mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              type="number"
              label="Radius Input (meter)"
              value={values.radiusInput}
              onChange={handleInputChange("radiusInput")}
              required
              error={Boolean(errors && errors.radiusInput)}
            />
            {errors && errors.radiusInput && (
              <span style={{ color: "red", fontSize: 12 }}>
                {errors.radiusInput}
              </span>
            )}
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default ProyekDetail;
